import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { upgradeRequestAPI } from '../utils/api';
import { 
  PlusIcon, 
  ChartBarIcon, 
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import UpgradeRequestForm from './UpgradeRequestForm';
import RequestList from './RequestList';
import ChangePasswordModal from './ChangePasswordModal';
import { formatDate } from '../utils/dateUtils';
import { formatBandwidth } from '../utils/bandwidthUtils';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  
  // Check if user needs to change password on mount
  React.useEffect(() => {
    if (user && !user.password_changed) {
      setForcePasswordChange(true);
      setShowChangePassword(true);
    } else if (user && user.password_changed) {
      setForcePasswordChange(false);
    }
  }, [user]);

  const { data: requests, loading: requestsLoading, refetch } = useApi(
    () => upgradeRequestAPI.getAll({ page: 1, limit: 50 }),
    [activeTab]
  );


  const handleRequestCreated = () => {
    setShowCreateForm(false);
    refetch();
  };

  const handlePasswordChangeClose = (success) => {
    if (success && forcePasswordChange) {
      setForcePasswordChange(false);
    }
    setShowChangePassword(false);
  };

  const getStatusCount = (status) => {
    const requestList = requests?.data?.requests || requests?.requests || [];
    return requestList.filter(req => req.status === status).length;
  };

  const totalRequests = (requests?.data?.requests || requests?.requests || []).length;
  const pendingCount = getStatusCount('pending');
  const approvedCount = getStatusCount('approved');
  const rejectedCount = getStatusCount('rejected');

  const stats = [
    {
      name: 'Total Requests',
      value: totalRequests,
      icon: ChartBarIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      name: 'Pending',
      value: pendingCount,
      icon: ClockIcon,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      name: 'Approved',
      value: approvedCount,
      icon: CheckCircleIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      name: 'Rejected',
      value: rejectedCount,
      icon: XCircleIcon,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    }
  ];

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'requests', name: 'My Requests', icon: DocumentTextIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="h-8 w-8 mr-3">
                <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Garuda Media" className="w-full h-full object-contain" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">
                Garuda Media - ISP Upgrade System
              </h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                Welcome, <span className="font-medium">{user?.username}</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowChangePassword(true)}
                  className="btn-secondary text-sm"
                >
                  Change Password
                </button>
                <button
                  onClick={logout}
                  className="btn-secondary text-sm"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="sm:flex sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
              <p className="mt-1 text-sm text-gray-600">
                Account ID: <span className="font-medium">{user?.account_id}</span>
                {user?.company_name && (
                  <> • Company: <span className="font-medium">{user.company_name}</span></>
                )}
                {user?.current_bandwidth && (
                  <> • Current Bandwidth: <span className="font-medium text-blue-600">{formatBandwidth(user.current_bandwidth)}</span></>
                )}
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button
                onClick={() => setShowCreateForm(true)}
                className="btn-primary flex items-center"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                New Request
              </button>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.name} className="card p-6">
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 ${stat.bgColor} p-3 rounded-lg`}>
                      <stat.icon className={`h-6 w-6 ${stat.color}`} />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                      <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {(requests?.data?.requests || requests?.requests || []).length > 0 && (
              <div className="card">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Recent Requests</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Account ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Current → New Bandwidth
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(requests?.data?.requests || requests?.requests || []).slice(0, 5).map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {request.account_id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {request.current_bandwidth} → {request.requested_bandwidth}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`status-badge status-${request.status}`}>
                              {request.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(request.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'requests' && (
          <RequestList />
        )}
      </div>

      {showCreateForm && (
        <UpgradeRequestForm
          onClose={() => setShowCreateForm(false)}
          onSuccess={handleRequestCreated}
        />
      )}
      
      {showChangePassword && (
        <ChangePasswordModal
          onClose={handlePasswordChangeClose}
          isForced={forcePasswordChange}
        />
      )}
    </div>
  );
};

export default Dashboard;